import { useMemo } from "react";
import { StyleSheet, Text, View, useWindowDimensions } from "react-native";
import type { TrackerSession, TrackerStat } from "@heraion/shared";

import { ChartHead, ChartReadout, chartTitleStyle } from "../charts/ChartHead";
import { usePointSelection, type PointSelection } from "../charts/pointSelection";
import { WagmiLineChart } from "../charts/WagmiLineChart";
import { IconButton } from "../primitives/IconButton";
import type { UiTheme } from "../theme";
import {
  buildTrackerChart,
  percentFormat,
  trackerReadout,
  type TrackerFormat,
} from "./exerciseTrackerModel";

// #389: one exercise of the tracker. The head names it and reads the picked
// session, the real value of every statistic with its unit; the chart draws
// one curve per statistic, the change since the first session in the range,
// over a 0% hairline, the lead curve's last change at its end. The key is
// the widget's, above all the charts, never one per chart. The panel mounts
// it again when the range, the statistics or the exercises change, so a
// tapped point never outlives the data it was read from.

/** Taller with three statistics or more, so the curves stay apart. */
const HEIGHT = { few: 80, many: 116 } as const;
/** Above this system text scale the readout stacks, one statistic a line. */
const STACK_FONT_SCALE = 1.2;

export function ExerciseTrackerChart({
  theme,
  name,
  sessions,
  stats,
  format,
  dateLabel,
  nowMs,
  emptyText,
  remove,
}: {
  theme: UiTheme;
  /** The exercise, and its machine when it has more than one in range. */
  name: string;
  sessions: readonly TrackerSession[];
  stats: readonly TrackerStat[];
  format: TrackerFormat;
  dateLabel: (atMs: number) => string;
  nowMs: number;
  /** Said when nothing in the range carries a chosen statistic: no session,
   *  or sessions that carry none of them (a bodyweight exercise's reps). */
  emptyText: string;
  /** The ✕ that stops tracking it. Absent: the last exercise, kept. */
  remove?: { a11yLabel: string; onPress: () => void };
}) {
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t, locale } = format;
  const chart = useMemo(
    () => buildTrackerChart(sessions, stats, t, dateLabel),
    [sessions, stats, t, dateLabel],
  );
  const yFormat = useMemo(() => percentFormat(t, locale), [t, locale]);
  const base = usePointSelection(chart.headline.length);
  const picked = chart.headline[base.selected];
  const session = picked ? sessions.find((s) => s.atMs === picked.x) : undefined;
  const { fontScale } = useWindowDimensions();
  const stacked = stats.length >= 3 || fontScale > STACK_FONT_SCALE;
  const readout = trackerReadout(session, stats, format, dateLabel, stacked);
  const selection: PointSelection = { ...base, readout };

  return (
    <View style={styles.block}>
      <View style={styles.head}>
        <View style={styles.headText}>
          {stacked ? (
            // The name alone on its line; the readout below it, full width.
            <Text style={chartTitleStyle(theme)}>{name}</Text>
          ) : (
            <ChartHead theme={theme} title={name} readout={readout} />
          )}
        </View>
        {remove ? (
          <IconButton
            theme={theme}
            name="close"
            variant="cancel"
            accessibilityLabel={remove.a11yLabel}
            onPress={remove.onPress}
            // 40 drawn, 44 to the finger (the web floor keeps 44 there).
            style={styles.remove}
            hitSlop={2}
          />
        ) : null}
      </View>
      {stacked && readout ? <ChartReadout theme={theme} readout={readout} /> : null}
      {chart.series.length === 0 ? (
        <Text style={styles.empty}>{emptyText}</Text>
      ) : (
        <WagmiLineChart
          series={chart.series}
          theme={theme}
          yFormat={yFormat}
          showMeta={false}
          nowMs={nowMs}
          selection={selection}
          height={stats.length >= 3 ? HEIGHT.many : HEIGHT.few}
          baseline={0}
          endLabel
          lastDot="line"
        />
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    block: { gap: s.s2 },
    head: { flexDirection: "row", alignItems: "flex-start", gap: s.s2 },
    headText: { flex: 1, minWidth: 0 },
    empty: { color: p.ink3, fontSize: ty.sizes.sm, fontStyle: "italic" },
    remove: { width: 40, height: 40 },
  });
}
